import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { investorsAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { InvestorFormDialog } from './investors/InvestorFormDialog'
import {
  Plus,
  Search,
  Landmark,
  ChevronLeft,
  ChevronRight,
  Loader2,
  UserX,
  Mail,
  Phone,
  LayoutGrid,
  List,
} from 'lucide-react'

const ROLE_LABELS: Record<string, string> = {
  INVESTOR: 'Sócio Investidor',
  ADMINISTRATOR: 'Sócio Administrador',
}

const ROLE_COLORS: Record<string, string> = {
  INVESTOR: 'bg-blue-100 text-blue-700',
  ADMINISTRATOR: 'bg-purple-100 text-purple-700',
}

export function Investors() {
  const navigate = useNavigate()
  const canCreate = usePermission('investors:create')

  const [viewMode, setViewMode] = useState<'card' | 'table'>(() =>
    (localStorage.getItem('viewMode:investors') as 'card' | 'table') ?? 'card'
  )
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const limit = viewMode === 'table' ? 15 : 9

  const { data, isLoading } = useQuery({
    queryKey: ['investors', { page, search, isActive: statusFilter, limit }],
    queryFn: () =>
      investorsAPI.list({
        page,
        limit,
        search: search || undefined,
        isActive: statusFilter === '' ? undefined : statusFilter === 'true',
      }),
  })

  const investors = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const handleViewMode = (v: 'card' | 'table') => {
    localStorage.setItem('viewMode:investors', v)
    setViewMode(v)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Investidores</h1>
          <p className="mt-1 text-neutral-600">Gerencie os investidores cadastrados</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-lg border p-0.5">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleViewMode('card')}
            >
              <LayoutGrid className="mr-1 h-3.5 w-3.5" />Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleViewMode('table')}
            >
              <List className="mr-1 h-3.5 w-3.5" />Tabela
            </Button>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Investidor
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por nome ou CPF/CNPJ..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>

        <Select
          value={statusFilter || 'ALL'}
          onValueChange={(value) => { setStatusFilter(value === 'ALL' ? '' : value); setPage(1) }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="true">Ativo</SelectItem>
            <SelectItem value="false">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : investors.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <UserX className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {search || statusFilter ? 'Nenhum investidor encontrado' : 'Nenhum investidor cadastrado'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {search || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Comece cadastrando seu primeiro investidor.'}
          </p>
          {canCreate && !search && !statusFilter && (
            <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />Cadastrar Primeiro Investidor
            </Button>
          )}
        </div>
      ) : viewMode === 'card' ? (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {investors.map((investor: any) => (
              <Card
                key={investor.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/investors/${investor.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Landmark className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base line-clamp-1">{investor.name}</CardTitle>
                    </div>
                    <Badge variant={investor.isActive ? 'default' : 'secondary'}>
                      {investor.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-neutral-500">{formatDocument(investor.document)}</p>
                  <Badge variant="outline" className={ROLE_COLORS[investor.role] || ''}>
                    {ROLE_LABELS[investor.role] || investor.role}
                  </Badge>
                  <div className="flex items-center gap-4 text-sm text-neutral-500">
                    {investor.email && (
                      <div className="flex items-center gap-1 truncate">
                        <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{investor.email}</span>
                      </div>
                    )}
                    {investor.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{investor.phone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                Mostrando {investors.length} de {pagination.total} investidores
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rounded-lg border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead className="font-semibold">Nome</TableHead>
                  <TableHead className="font-semibold">CPF/CNPJ</TableHead>
                  <TableHead className="font-semibold">Papel</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Telefone</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {investors.map((investor: any) => (
                  <TableRow
                    key={investor.id}
                    className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => navigate(`/investors/${investor.id}`)}
                  >
                    <TableCell className="font-medium">{investor.name}</TableCell>
                    <TableCell className="text-neutral-500 text-sm">{formatDocument(investor.document) || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={ROLE_COLORS[investor.role] || ''}>
                        {ROLE_LABELS[investor.role] || investor.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-neutral-500 text-sm max-w-[180px] truncate">
                      {investor.email || '—'}
                    </TableCell>
                    <TableCell className="text-neutral-500 text-sm">{investor.phone || '—'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={investor.isActive ? 'default' : 'secondary'}>
                        {investor.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                Mostrando {investors.length} de {pagination.total} investidores
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <InvestorFormDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </div>
  )
}
